import React from 'react';
import { Container, Navbar, Nav, NavDropdown, Form, Spinner } from 'react-bootstrap';
import { BrowserRouter as Router, Routes, Route, Link, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import { registerLocale } from 'react-datepicker';
import { af } from 'date-fns/locale/af';
import 'bootstrap/dist/css/bootstrap.min.css';
import 'react-toastify/dist/ReactToastify.css';
import 'react-datepicker/dist/react-datepicker.css';
import './styles/DatePicker.css';
import './App.css';
import { AuthProvider, useAuth } from './context/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import Login from './pages/Login';
import Register from './pages/Register';
import Dashboard from './pages/Dashboard';
import AccountHolders from './pages/AccountHolders';
import FarmWorkPage from './pages/FarmWork';
import Payments from './pages/Payments';
import UserManagement from './pages/UserManagement';
import UserProfile from './pages/UserProfile';
import DashboardSettings from './pages/DashboardSettings';
import { AppCurrency, AppLanguage, LocalizationProvider, useLocalization } from './context/LocalizationContext';
import ChatBot from './components/ChatBot';
import { TRUST_ACCOUNT_KIND_KEY } from './utils/trustAccount';
import Maintenance from './pages/Maintenance';
import { MaintenanceProvider, useMaintenance } from './context/MaintenanceContext';
import './styles/Maintenance.css';

registerLocale('af', af);

function AppContent() {
  const { isAuthenticated, user, logout, isLoading } = useAuth();
  const { t, language, currency, setLanguage, setCurrency } = useLocalization();
  const navigate = useNavigate();
  const location = useLocation();

  const isActivePath = (path: string) => location.pathname === path;
  const isSettingsActive = ['/admin-settings', '/dashboard-settings', '/users'].includes(location.pathname);
  const [selectedTrustAccountKind, setSelectedTrustAccountKind] = React.useState<string | null>(localStorage.getItem(TRUST_ACCOUNT_KIND_KEY));

  React.useEffect(() => {
    const sync = () => setSelectedTrustAccountKind(localStorage.getItem(TRUST_ACCOUNT_KIND_KEY));
    window.addEventListener('trust-account-changed', sync);
    window.addEventListener('storage', sync);
    return () => {
      window.removeEventListener('trust-account-changed', sync);
      window.removeEventListener('storage', sync);
    };
  }, []);

  // Lightweight UI gating: account id 2 is seeded savings account in mock mode.
  const isSavingsMode = (selectedTrustAccountKind || '').toLowerCase() === 'savings';

  if (isLoading) {
    return (
        <div className="app-startup-loading">
          <div className="app-startup-logo">
            <div className="app-startup-brand">Savannah Accounts</div>
          </div>
          <div className="app-startup-spinner-wrap">
            <Spinner animation="border" role="status" aria-label={t('loadingApplication')} />
            <span className="app-startup-label">{t('loadingApplication')}</span>
          </div>
        </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Navigate to="/login" replace />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  return (
    <div className="d-flex flex-column min-vh-100 app-shell">
      <Navbar bg="dark" expand="lg" className="navbar-dark sticky-top app-navbar app-navbar-surface">
        <Container>
          <Navbar.Brand as={Link} to="/" className="fw-bold">
            {t('appTitle')}
          </Navbar.Brand>
          <Navbar.Toggle aria-controls="basic-navbar-nav" />
          <Navbar.Collapse id="basic-navbar-nav">
            <Nav className="ms-auto app-nav-links">
              <Nav.Link as={Link} to="/" className={isActivePath('/') ? 'is-active' : ''}>
                {t('dashboard')}
              </Nav.Link>
              {!isSavingsMode && (
                <Nav.Link as={Link} to="/account-holders" className={isActivePath('/account-holders') ? 'is-active' : ''}>
                  {t('accountHolders')}
                </Nav.Link>
              )}
              {!isSavingsMode && (
                <Nav.Link as={Link} to="/expenses" className={isActivePath('/expenses') || isActivePath('/farm-work') ? 'is-active' : ''}>
                  {t('farmWork')}
                </Nav.Link>
              )}
              <Nav.Link as={Link} to="/payments" className={isActivePath('/payments') ? 'is-active' : ''}>
                {t('accountOverviewTitle')}
              </Nav.Link>

              {user?.role === 'Admin' && (
                <NavDropdown
                  title={t('settings')}
                  id="app-settings-dropdown"
                  align="end"
                  className={isSettingsActive ? 'is-active' : ''}
                >
                  <NavDropdown.Item as={Link} to="/admin-settings" active={isActivePath('/admin-settings') || isActivePath('/dashboard-settings')}>
                    {t('dashboardSettings')}
                  </NavDropdown.Item>
                  <NavDropdown.Item as={Link} to="/users" active={isActivePath('/users')}>
                    {t('userManagement')}
                  </NavDropdown.Item>
                </NavDropdown>
              )}

              <NavDropdown
                title={(
                  <span
                    className="app-localization-trigger"
                    aria-label={t('profilePreferences')}
                    title={`${t('language')} / ${t('currency')}`}
                  >
                    Aa
                  </span>
                )}
                id="app-localization-dropdown"
                align="end"
                className="app-localization-dropdown"
              >
                <NavDropdown.Header>{t('profilePreferences')}</NavDropdown.Header>
                <NavDropdown.ItemText className="app-localization-panel" onClick={(event) => event.preventDefault()}>
                  <div className="app-localization-field">
                    <label htmlFor="nav-localization-language">{t('language')}</label>
                    <Form.Select
                      id="nav-localization-language"
                      size="sm"
                      value={language}
                      onClick={(event) => event.stopPropagation()}
                      onChange={(event) => setLanguage(event.target.value as AppLanguage)}
                    >
                      <option value="en">{t('english')}</option>
                      <option value="af">{t('afrikaans')}</option>
                    </Form.Select>
                  </div>
                  <div className="app-localization-field mt-2">
                    <label htmlFor="nav-localization-currency">{t('currency')}</label>
                    <Form.Select
                      id="nav-localization-currency"
                      size="sm"
                      value={currency}
                      onClick={(event) => event.stopPropagation()}
                      onChange={(event) => setCurrency(event.target.value as AppCurrency)}
                    >
                      <option value="USD">{t('currencyUsd')}</option>
                      <option value="ZAR">{t('currencyZar')}</option>
                    </Form.Select>
                  </div>
                </NavDropdown.ItemText>
              </NavDropdown>

              <Nav.Link
                className="ms-lg-2"
                onClick={() => {
                  logout();
                  navigate('/login', { replace: true });
                }}
              >
                {t('logout')}
              </Nav.Link>
            </Nav>
          </Navbar.Collapse>
        </Container>
      </Navbar>

      <main className="flex-grow-1 app-main-surface">
        <Routes>
          <Route
            path="/"
            element={<ProtectedRoute component={Dashboard} />}
          />
          {!isSavingsMode && (
            <Route
              path="/account-holders"
              element={<ProtectedRoute component={AccountHolders} />}
            />
          )}
          {!isSavingsMode && (
            <Route
              path="/expenses"
              element={<ProtectedRoute component={FarmWorkPage} />}
            />
          )}
          {!isSavingsMode && (
            <Route
              path="/farm-work"
              element={<Navigate to="/expenses" replace />}
            />
          )}
          <Route
            path="/payments"
            element={<ProtectedRoute component={Payments} />}
          />
          <Route
            path="/statements"
            element={<Navigate to="/payments" replace />}
          />
          <Route
            path="/users"
            element={<ProtectedRoute component={UserManagement} requiredRole="Admin" />}
          />
          <Route
            path="/profile"
            element={<ProtectedRoute component={UserProfile} />}
          />
          <Route
            path="/admin-settings"
            element={<ProtectedRoute component={DashboardSettings} requiredRole="Admin" />}
          />
          <Route
            path="/dashboard-settings"
            element={<Navigate to="/admin-settings" replace />}
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <ChatBot />
      </main>

      <footer className="bg-dark text-white text-center py-4 mt-5 app-footer-surface">
        <Container>
          <p className="mb-0">
            &copy; 2026 Savannah Accounts System. Developed and supported by{' '}
            <a href="mailto:d89strydom@hotmail.co.za">DC Consulting</a>.
          </p>
        </Container>
      </footer>
    </div>
  );
}

function ScrollToTopButton() {
  const [isVisible, setIsVisible] = React.useState(false);

  React.useEffect(() => {
    const onScroll = () => {
      setIsVisible(window.scrollY > 220);
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();

    return () => {
      window.removeEventListener('scroll', onScroll);
    };
  }, []);

  return (
    <button
      type="button"
      className={`app-scroll-top-btn ${isVisible ? 'is-visible' : ''}`}
      onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
      aria-label="Scroll to top"
      title="Scroll to top"
    >
      <svg viewBox="0 0 16 16" className="app-scroll-top-icon" focusable="false" aria-hidden="true">
        <path d="M8 13V4" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
        <path d="M4.5 7.5L8 4l3.5 3.5" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </button>
  );
}

function MaintenanceCheck() {
  const { isMaintenanceMode, isMaintenanceLoading } = useMaintenance();
  const { t } = useLocalization();

  if (isMaintenanceLoading) {
    return (
      <div className="app-startup-loading">
        <div className="app-startup-logo">
          <div className="app-startup-brand">Savannah Accounts</div>
        </div>
        <div className="app-startup-spinner-wrap">
          <Spinner animation="border" role="status" aria-label={t('loadingApplication')} />
          <span className="app-startup-label">{t('loadingApplication')}</span>
        </div>
      </div>
    );
  }

  if (isMaintenanceMode) {
    return <Maintenance />;
  }

  return <AppContent />;
}

function App() {
  return (
    <Router>
      <MaintenanceProvider>
        <AuthProvider>
          <LocalizationProvider>
            <MaintenanceCheck />
            <ScrollToTopButton />
          </LocalizationProvider>
        </AuthProvider>
      </MaintenanceProvider>
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
    </Router>
  );
}

export default App;
